import { useEffect, useState } from 'react';

import { useRouter } from 'next/router';

import useProfileSizes from '~/pdp/utils/useProfileSizes';
import useGender from '~/shared/utils/useGender';

import { getAutoFilterParams } from '../utils/autofilters';
import { getNewQueryParamString, QueryParamBehavior } from '../utils/routing';

export const useAutoFilters = ({ builderState, gridGender }) => {
  const router = useRouter();
  const [autofilterApplied, setAutofilterApplied] = useState(false);
  const { profileSizes } = useProfileSizes({ isPdp: false });
  const { gender: userGender } = useGender();

  // TODO: check if this handles ABT variants
  const automaticSizeFiltering =
    builderState?.content?.data?.automaticSizeFiltering;

  // TODO: figure out why size_one_piece is detected
  // `autofilterParams` does not include `size_one_piece` but `Object.entries(autofilterParams)` does
  const autofilterParams = getAutoFilterParams({
    gridGender,
    profileSizes,
    userGender,
    automaticSizeFilteringType: automaticSizeFiltering,
  });

  const nonEmptyAutofilterParams = Object.entries(autofilterParams).reduce(
    (acc, [key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        return { ...acc, [key]: value };
      }

      if (typeof value === 'string' && value !== '') {
        return { ...acc, [key]: value };
      }

      return acc;
    },
    {}
  );

  useEffect(() => {
    if (!nonEmptyAutofilterParams) {
      return;
    }

    /**
     * Flow for autofilters enabled:
     * 1st render: autofilters are not applied, apply autofilters via route change and set autofilterApplied to true
     * 2nd render: autofilterApplied is true but autofilters are not applied in the url, do nothing
     * 3rd render: autofilterApplied is true and autofilters are applied in the url, do nothing
     * */
    if (!autofilterApplied) {
      const [aspathname, query] = router.asPath.split('?');
      const newQueryParamString = getNewQueryParamString({
        query,
        newQueryParams: nonEmptyAutofilterParams,
        queryParamBehavior: QueryParamBehavior.APPEND,
      });

      if (!newQueryParamString) {
        return;
      }

      router.push(router.pathname, `${aspathname}?${newQueryParamString}`, {
        shallow: true,
      });

      /**
       * Due to the async nature of router.push(), the component may re-render with autofilterApplied as true
       * before the autofilters are actually applied in the url. This should still prevent future autofilters to be applied
       * but it might cause extraneous api calls downstream. This isn't too big of a deal since the api calls are cached via react-query.
       * TODO: figure out a way to update state only after the autofilters are applied in the url and navigate immediately to skip the extra 2nd render
       * */
      setAutofilterApplied(true);
    }
  }, [autofilterApplied, nonEmptyAutofilterParams, router]);
};
